import {
  SharedValue,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
} from 'react-native-reanimated'

import {
  backgroundColor,
  borderColor,
  labelInlineColor,
  labelTopColor,
  requiredIndicatorColor,
} from './styles'

interface Props {
  focus?: SharedValue<number>
  success?: SharedValue<number>
  error?: SharedValue<number>
  disabled?: SharedValue<number>
  readonly?: SharedValue<number>
}

export const useAnimatedInputBorder = ({
  focus,
  success,
  error,
  disabled,
  readonly,
}: Props) => {
  const border = borderColor

  const animatedStyle = useAnimatedStyle(() => {
    const readonlyColor = interpolateColor(
      readonly?.value || 0,
      [0, 1],
      [border.default, border.readonly]
    )

    const focusColor = interpolateColor(
      focus?.value || 0,
      [0, 1],
      [readonlyColor, border.focus]
    )

    const statusColor = interpolateColor(
      error?.value ? -1 : !success?.value ? 0 : 1,
      [-1, 0, 1],
      [border.error, focusColor, border.success]
    )

    const disabledColor = interpolateColor(
      disabled?.value || 0,
      [0, 1],
      [statusColor, border.disabled]
    )
    const stroke = 1 //StyleSheet.hairlineWidth

    const resolve = (value?: number) => {
      'worklet'
      return value ?? 0
    }

    const borderValue =
      resolve(focus?.value) + resolve(error?.value) + resolve(success?.value)
    const borderWidth = interpolate(
      Math.min(1, borderValue),
      [0, 1],
      [stroke, stroke * 2]
    )

    return {
      borderColor: disabledColor,
      borderWidth,
    }
  })

  return animatedStyle
}

export const useAnimatedInputBackground = ({
  focus,
  success,
  error,
  disabled,
  readonly,
}: Props) => {
  const background = backgroundColor

  const animatedStyle = useAnimatedStyle(() => {
    const readonlyColor = interpolateColor(
      readonly?.value || 0,
      [0, 1],
      [background.default, background.readonly]
    )

    const disabledColor = interpolateColor(
      disabled?.value || 0,
      [0, 1],
      [readonlyColor, background.disabled]
    )

    const statusColor = interpolateColor(
      error?.value ? -1 : !success?.value ? 0 : 1,
      [-1, 0, 1],
      [background.error, disabledColor, background.success]
    )

    const focusColor = interpolateColor(
      focus?.value || 0,
      [0, 1],
      [statusColor, background.focus]
    )

    return {
      backgroundColor: focusColor,
    }
  })

  return animatedStyle
}

export const useAnimatedInputLabelInline = ({
  focus,
  success,
  error,
  disabled,
  readonly,
}: Props) => {
  const text = labelInlineColor

  const animatedStyle = useAnimatedStyle(() => {
    const readonlyColor = interpolateColor(
      readonly?.value || 0,
      [0, 1],
      [text.default, text.readonly]
    )

    const disabledColor = interpolateColor(
      disabled?.value || 0,
      [0, 1],
      [readonlyColor, text.disabled]
    )

    const focusColor = interpolateColor(
      focus?.value || 0,
      [0, 1],
      [disabledColor, text.focus]
    )

    const statusColor = interpolateColor(
      error?.value ? -1 : !success?.value ? 0 : 1,
      [-1, 0, 1],
      [text.error, focusColor, text.success]
    )

    return {
      color: statusColor,
    }
  })

  return animatedStyle
}

export const useAnimatedInputLabelTop = ({
  focus,
  success,
  error,
  disabled,
  readonly,
}: Props) => {
  const text = labelTopColor

  const animatedStyle = useAnimatedStyle(() => {
    const readonlyColor = interpolateColor(
      readonly?.value || 0,
      [0, 1],
      [text.default, text.readonly]
    )

    const disabledColor = interpolateColor(
      disabled?.value || 0,
      [0, 1],
      [readonlyColor, text.disabled]
    )

    const focusColor = interpolateColor(
      focus?.value || 0,
      [0, 1],
      [disabledColor, text.focus]
    )

    const statusColor = interpolateColor(
      error?.value ? -1 : !success?.value ? 0 : 1,
      [-1, 0, 1],
      [text.error, focusColor, text.success]
    )

    return {
      color: statusColor,
    }
  })

  return animatedStyle
}

export const useAnimatedInputRequiredIndicator = ({
  focus,
  success,
  error,
  disabled,
  readonly,
}: Props) => {
  const text = requiredIndicatorColor

  const animatedStyle = useAnimatedStyle(() => {
    const readonlyColor = interpolateColor(
      readonly?.value || 0,
      [0, 1],
      [text.default, text.readonly]
    )

    const disabledColor = interpolateColor(
      disabled?.value || 0,
      [0, 1],
      [readonlyColor, text.disabled]
    )

    const focusColor = interpolateColor(
      focus?.value || 0,
      [0, 1],
      [disabledColor, text.focus]
    )

    const statusColor = interpolateColor(
      error?.value ? -1 : !success?.value ? 0 : 1,
      [-1, 0, 1],
      [text.error, focusColor, text.success]
    )

    return {
      color: statusColor,
    }
  })

  return animatedStyle
}
